import React, { useContext, useEffect, useState } from "react";
import { ProblemDetails } from "../../models/Http";
import { CategoryId, ProductMinimalModel } from "../../models/Product";
import {
  StocktakingOverviewListItem,
  StoreStocktakingItem
} from "../../models/Stocktaking";
import { UserContext } from "../../UserProvider";
import { useDispatch, useSelector } from "react-redux";
import { hasRole } from "../../models/User";
import { permissions } from "../../constants";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import { AlertTitle } from "@material-ui/lab";
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography
} from "@material-ui/core";
import { AppState } from "../../store";
import { isOk, Result } from "../../result";
import { thunkFetchAwaitingStocktakingItems } from "../../store/stocktaking/actions";
import { getComparator, SortOrder, stableSort } from "../../tableSorting";
import moment from "moment";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import { StoreMultiSelect } from "../../components/selects/StoreMultiSelect";
import { CategoryMultiSelect } from "../../components/selects/CategoryMultiSelect";
import { ConfirmDialog } from "../../components/dialogs/ConfirmDialog";
import * as StocktakingApi from "../../api/stocktakingApi";
import { useI18n } from "../../hooks/useI18n";

const useStyles = makeStyles(theme => ({
  loading: {
    marginTop: theme.spacing(10),
    display: "flex",
    justifyContent: "center",
    alignContent: "center"
  },
  section: {
    margin: theme.spacing(2)
  },
  sectionHeader: {
    marginBottom: theme.spacing(2)
  },
  field: {
    display: "flex",
    justifyContent: "center"
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  buttons: {
    margin: theme.spacing(2),
    position: "fixed",
    bottom: 0,
    right: 0
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2)
  },
  title: {
    flex: "1 1 100%"
  },
  table: {},
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  },
  createDialog: {
    minWidth: 400
  },
  createDialogCheckbox: {
    marginTop: theme.spacing(4)
  }
}));

const daysBack = [0, 1, 7, 14, 20, 30, 60];

interface NotAllowed {
  type: "NOT_ALLOWED";
}

export interface UnexpectedError {
  type: "UNEXPECTED_ERROR";
  problem: ProblemDetails;
}

interface LoadItems {
  type: "LOAD_ITEMS";
}

interface Loading {
  type: "LOADING";
}

interface Overview {
  type: "OVERVIEW";
  items: ReadonlyArray<StocktakingOverviewListItem>;
  filters: StocktakingListFilter;
}

interface ShowCreateDialog {
  type: "SHOW_CREATE_DIALOG";
  items: ReadonlyArray<StocktakingOverviewListItem>;
  filters: StocktakingListFilter;
  createState: CreateStocktaking;
}

interface CreateStocktaking {
  type: "CREATE_STOCKTAKING";
  storeIds: ReadonlyArray<string>;
  categoryIds: ReadonlyArray<CategoryId>;
  allStores: boolean;
  allCategories: boolean;
  daysBack: number;
}

interface Creating {
  type: "CREATING";
}

type State =
  | NotAllowed
  | UnexpectedError
  | LoadItems
  | Loading
  | Overview
  | ShowCreateDialog
  | CreateStocktaking
  | Creating;

interface HeadCell {
  id: keyof StocktakingOverviewListItem;
  label: string;
  numeric: boolean;
}

interface StocktakingListFilter {
  searchPhrase: string;
  order: SortOrder;
  orderBy: keyof StocktakingOverviewListItem;
  page: number;
  rowsPerPage: number;
}

const initialStocktakingListFilter = (): StocktakingListFilter => ({
  order: "asc",
  orderBy: "storeName",
  page: 0,
  rowsPerPage: 25,
  searchPhrase: ""
});

const headCells: HeadCell[] = [
  { id: "storeName", numeric: false, label: "Store" },
  { id: "gtin", numeric: false, label: "GTIN" },
  { id: "productName", numeric: false, label: "Product" },
  { id: "categoryName", numeric: false, label: "Category" },
  { id: "created", numeric: false, label: "Created" }
];

interface EnhancedTableHeadProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof StocktakingOverviewListItem
  ) => void;
  filters: StocktakingListFilter;
  t: (key: string) => string;
}

function EnhancedTableHead(props: EnhancedTableHeadProps) {
  const { classes, onRequestSort, filters, t } = props;
  const createSortHandler =
    (property: keyof StocktakingOverviewListItem) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={"default"}
            sortDirection={
              filters.orderBy === headCell.id ? filters.order : false
            }
          >
            <TableSortLabel
              active={filters.orderBy === headCell.id}
              direction={
                filters.orderBy === headCell.id ? filters.order : "asc"
              }
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {filters.orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {filters.order === "desc"
                    ? t("common.messages.sortedDescending")
                    : t("common.messages.sortedAscending")}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export const StocktakingOverview = () => {
  const classes = useStyles();
  const user = useContext(UserContext);
  const dispatch = useDispatch();
  const [state, setState] = useState<State>(
    user.type === "ANONYMOUS" || !hasRole(user, permissions.stocktakingManager)
      ? { type: "NOT_ALLOWED" }
      : { type: "LOAD_ITEMS" }
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const awaitingStoreStocktakingItems = useSelector<
    AppState,
    Result<ProblemDetails, ReadonlyArray<StoreStocktakingItem>>
  >(s => s.awaitingStoreStocktakingItems);
  const { t } = useI18n();

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof StocktakingOverviewListItem
  ) => {
    if (state.type !== "OVERVIEW") {
      return;
    }

    const isAsc =
      state.filters.orderBy === property && state.filters.order === "asc";

    setState({
      ...state,
      filters: {
        ...state.filters,
        order: isAsc ? "desc" : "asc",
        orderBy: property
      }
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    if (state.type !== "OVERVIEW") {
      return;
    }

    setState({ ...state, filters: { ...state.filters, page: newPage } });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (state.type !== "OVERVIEW") {
      return;
    }

    setState({
      ...state,
      filters: {
        ...state.filters,
        page: 0,
        rowsPerPage: parseInt(event.target.value, 10)
      }
    });
  };

  const handleOpenMoreMenu = (evt: React.MouseEvent<HTMLButtonElement>) => {
    if (state.type !== "OVERVIEW") {
      return;
    }

    setAnchorEl(evt.currentTarget);
  };

  const handleCloseMoreMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenCreateDialog = () => {
    if (state.type !== "OVERVIEW") {
      return;
    }

    handleCloseMoreMenu();

    return setState({
      ...state,
      type: "SHOW_CREATE_DIALOG",
      createState: {
        type: "CREATE_STOCKTAKING",
        allStores: true,
        storeIds: [],
        allCategories: true,
        categoryIds: [],
        daysBack: 30
      }
    });
  };

  const handleCloseCreateDialog = () => {
    if (state.type !== "SHOW_CREATE_DIALOG") {
      return;
    }

    return setState({ ...state, type: "OVERVIEW" });
  };

  const handleCancelCreateDialog = () => {
    if (state.type !== "SHOW_CREATE_DIALOG") {
      return;
    }

    return setState({ ...state, type: "OVERVIEW" });
  };

  const handleConfirmCreateDialog = (): boolean => {
    if (state.type !== "SHOW_CREATE_DIALOG") {
      return false;
    }

    setState(state.createState);

    return true;
  };

  const emptyRows =
    state.type === "OVERVIEW"
      ? state.filters.rowsPerPage -
        Math.min(
          state.filters.rowsPerPage,
          state.items.length - state.filters.page * state.filters.rowsPerPage
        )
      : 0;

  const getProductConcatName = (product: ProductMinimalModel): string => {
    let rendered = "";

    if (product.brandName) {
      rendered += product.brandName;
    }

    if (rendered) {
      rendered += ", ";
    }
    rendered += product.name;

    if (product.size) {
      if (rendered) {
        rendered += ", ";
      }

      rendered += product.size;
    }

    return rendered;
  };

  useEffect(() => {
    console.log(state.type);

    switch (state.type) {
      case "LOAD_ITEMS":
        dispatch(thunkFetchAwaitingStocktakingItems());

        return setState({ type: "LOADING" });

      case "CREATE_STOCKTAKING":
        setState({ ...state, type: "CREATING" });

        StocktakingApi.create({ ...state }).then(result => {
          if (!isOk(result)) {
            console.error(result.details);
            return setState({
              type: "UNEXPECTED_ERROR",
              problem: result.details
            });
          }

          return setState({ type: "LOAD_ITEMS" });
        });

        break;
    }
  }, [state.type]);

  useEffect(() => {
    const toListItemFromStore = (
      item: StoreStocktakingItem
    ): StocktakingOverviewListItem => {
      return {
        categoryName: item.product.category.name,
        created: item.created,
        gtin: item.product.gtin,
        id: item.id,
        productName: getProductConcatName(item.product),
        storeName: item.storeName as any
      };
    };

    if (isOk(awaitingStoreStocktakingItems)) {
      setState({
        type: "OVERVIEW",
        items: awaitingStoreStocktakingItems.value.map(toListItemFromStore),
        filters: initialStocktakingListFilter()
      });
    } else if (
      !isOk(awaitingStoreStocktakingItems) &&
      awaitingStoreStocktakingItems.details
    ) {
      setState({
        type: "UNEXPECTED_ERROR",
        problem: awaitingStoreStocktakingItems.details
      });
    }
  }, [awaitingStoreStocktakingItems]);

  switch (state.type) {
    case "UNEXPECTED_ERROR":
      return (
        <Alert severity="error">
          <AlertTitle>
            {state.problem.status}, {state.problem.title}
          </AlertTitle>
          {state.problem.detail} <strong>{state.problem.traceId}</strong>
        </Alert>
      );

    case "NOT_ALLOWED":
      return (
        <Alert severity="warning">
          {t("common.messages.notAllowedToSeeThisView")}
        </Alert>
      );

    case "LOAD_ITEMS":
    case "LOADING":
    case "CREATE_STOCKTAKING":
    case "CREATING":
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );
    case "OVERVIEW":
    case "SHOW_CREATE_DIALOG":
      return (
        <Paper className={classes.paper}>
          <Toolbar>
            <Typography
              className={classes.title}
              variant="h6"
              id="tableTitle"
              component="div"
            >
              Awaiting stocktaking
            </Typography>
            <Tooltip title={t("common.tooltip.more")}>
              <IconButton aria-label="more" onClick={handleOpenMoreMenu}>
                <MoreHorizIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleCloseMoreMenu}
            >
              <MenuItem onClick={handleOpenCreateDialog}>
                {t("common.actions.createNew")}
              </MenuItem>
            </Menu>
          </Toolbar>
          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size={"small"}
              aria-label={t("common.messages.enhancedTable")}
            >
              <EnhancedTableHead
                classes={classes}
                filters={state.filters}
                onRequestSort={handleRequestSort}
                t={t}
              />
              <TableBody>
                {stableSort<StocktakingOverviewListItem>(
                  state.items,
                  getComparator(state.filters.order, state.filters.orderBy)
                )
                  .slice(
                    state.filters.page * state.filters.rowsPerPage,
                    state.filters.page * state.filters.rowsPerPage +
                      state.filters.rowsPerPage
                  )
                  .map(item => (
                    <TableRow hover>
                      <TableCell>{item.storeName}</TableCell>
                      <TableCell>{item.gtin}</TableCell>
                      <TableCell>{item.productName}</TableCell>
                      <TableCell>{item.categoryName}</TableCell>
                      <TableCell>
                        {moment(item.created).format("YYYY-MM-DD HH:mm")}
                      </TableCell>
                    </TableRow>
                  ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 33 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={state.items.length}
            rowsPerPage={state.filters.rowsPerPage}
            page={state.filters.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <ConfirmDialog
            visible={state.type === "SHOW_CREATE_DIALOG"}
            titleText={t("common.actions.createStocktaking")}
            confirmText={"Create"}
            cancelText={"Cancel"}
            onClose={handleCloseCreateDialog}
            onCancel={handleCancelCreateDialog}
            onConfirm={handleConfirmCreateDialog}
          >
            <div className={classes.createDialog}>
              <FormControl className={classes.field}>
                <InputLabel id="period-select-label">
                  Not processed the last
                </InputLabel>
                <Select
                  labelId="period-select-label"
                  value={
                    state.type === "SHOW_CREATE_DIALOG"
                      ? state.createState.daysBack
                      : undefined
                  }
                  onChange={e => {
                    if (state.type !== "SHOW_CREATE_DIALOG") {
                      return;
                    }

                    return setState({
                      ...state,
                      createState: {
                        ...state.createState,
                        daysBack: Number(e.target.value)
                      }
                    });
                  }}
                  autoWidth={true}
                >
                  {daysBack.map(value => (
                    <MenuItem key={value} value={value}>
                      {`${value} days`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                className={classes.createDialogCheckbox}
                control={
                  <Checkbox
                    checked={
                      state.type === "SHOW_CREATE_DIALOG" &&
                      state.createState.allStores
                    }
                    onChange={e => {
                      if (state.type !== "SHOW_CREATE_DIALOG") {
                        return;
                      }

                      return setState({
                        ...state,
                        createState: {
                          ...state.createState,
                          allStores: e.target.checked,
                          storeIds: []
                        }
                      });
                    }}
                  />
                }
                label={t("common.formFields.allStores")}
              />
              <StoreMultiSelect
                disabled={
                  state.type === "SHOW_CREATE_DIALOG" &&
                  state.createState.allStores
                }
                className={classes.field}
                filterOpened={true}
                value={
                  state.type === "SHOW_CREATE_DIALOG" &&
                  state.createState.storeIds
                    ? state.createState.storeIds
                    : []
                }
                onSelected={stores => {
                  if (state.type !== "SHOW_CREATE_DIALOG") {
                    return;
                  }

                  return setState({
                    ...state,
                    createState: {
                      ...state.createState,
                      storeIds: stores.length > 0 ? stores.map(x => x.id) : []
                    }
                  });
                }}
              />
              <FormControlLabel
                className={classes.createDialogCheckbox}
                control={
                  <Checkbox
                    checked={
                      state.type === "SHOW_CREATE_DIALOG" &&
                      state.createState.allCategories
                    }
                    onChange={e => {
                      if (state.type !== "SHOW_CREATE_DIALOG") {
                        return;
                      }

                      return setState({
                        ...state,
                        createState: {
                          ...state.createState,
                          allCategories: e.target.checked,
                          categoryIds: []
                        }
                      });
                    }}
                  />
                }
                label={t("common.formFields.allCategories")}
              />
              <CategoryMultiSelect
                disabled={
                  state.type === "SHOW_CREATE_DIALOG" &&
                  state.createState.allCategories
                }
                className={classes.field}
                value={
                  state.type === "SHOW_CREATE_DIALOG" &&
                  state.createState.categoryIds
                    ? state.createState.categoryIds
                    : []
                }
                onSelected={categories => {
                  if (state.type !== "SHOW_CREATE_DIALOG") {
                    return;
                  }

                  return setState({
                    ...state,
                    createState: {
                      ...state.createState,
                      categoryIds:
                        categories.length > 0 ? categories.map(x => x.id) : []
                    }
                  });
                }}
              />
            </div>
          </ConfirmDialog>
        </Paper>
      );
  }
};
